import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { MapPin, Clock, Lock } from "lucide-react";
import { HiredSignalBox } from "../components/HiredSignalBox";
import { setPageMeta } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicJobPage() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [needsReference, setNeedsReference] = useState(false);
  const [refForm, setRefForm] = useState({ employer_name: "", employer_phone: "", property_name: "" });
  const [refError, setRefError] = useState("");
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  useEffect(() => {
    axios
      .get(`${API}/public/vaga/${jobId}`)
      .then((res) => {
        setJob(res.data);
        const j = res.data;
        setPageMeta({
          title: `${j.title}${j.location ? ` em ${j.location}` : ""}${j.salary_range ? ` – ${j.salary_range}` : ""} | HeveaTalent`,
          description: `Vaga na heveicultura: ${j.title}${j.location ? ` em ${j.location}` : ""}. Cadastre-se grátis no HeveaTalent e fale direto com quem contrata.`,
          canonical: `https://heveatalent.com.br/vaga/${jobId}`,
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => {
    if (job && localStorage.getItem("token")) revealContact();
  }, [job]);

  const revealContact = async () => {
    try {
      const res = await axios.get(`${API}/vaga/${jobId}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContact(res.data);
      setNeedsReference(false);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail === "reference_required") {
        setNeedsReference(true);
      } else if (e.response?.status === 401) {
        localStorage.removeItem("token");
        window.location.reload();
      }
    }
  };

  const submitReference = async (ev) => {
    ev.preventDefault();
    setRefError("");
    setSaving(true);
    try {
      await axios.post(`${API}/professional/references`, refForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await revealContact();
    } catch (e) {
      setRefError(e.response?.data?.detail || "Erro ao salvar a referência");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <p className="text-green-800">Carregando vaga...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="public-job-not-found">
          <CardContent className="pt-8 pb-8">
            <p className="text-4xl mb-3">🌳</p>
            <h2 className="text-xl font-bold text-gray-900">Esta vaga foi encerrada ou preenchida</h2>
            <p className="text-sm text-gray-600 mt-2">Mas há outras oportunidades esperando por você.</p>
            <Link to="/vagas">
              <Button className="mt-4 bg-green-700 hover:bg-green-800" data-testid="public-job-goto-platform">
                Ver vagas abertas →
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50" data-testid="public-job-page">
      <header className="bg-green-900 text-white py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-bold text-lg">🌳 HeveaTalent</Link>
          <Link
            to="/?cadastro=vaga"
            onClick={() => localStorage.setItem("pending_vaga", JSON.stringify({ id: jobId, ts: Date.now() }))}
          >
            <Button size="sm" variant="outline" className="bg-white text-green-900 hover:bg-green-100" data-testid="public-job-header-cta">
              Entrar / Cadastrar grátis
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <Card data-testid="public-job-card">
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-sky-100 text-sky-800">VAGA</Badge>
              {job.urgent && <Badge className="bg-red-100 text-red-700" data-testid="public-job-urgent">🔥 URGENTE</Badge>}
              {job.kind === "externa" && job.source_name && (
                <Badge variant="outline" className="text-[10px]">via {job.source_name}</Badge>
              )}
            </div>
            <CardTitle className="text-xl sm:text-2xl mt-1">{job.title}</CardTitle>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mt-1">
              {job.location && (
                <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" />{job.location}</span>
              )}
              {job.job_type && (
                <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{job.job_type}</span>
              )}
              {job.salary_range && <Badge variant="outline">💰 {job.salary_range}</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {job.description && <p className="text-gray-700 whitespace-pre-line">{job.description}</p>}
            {job.requirements && (
              <div>
                <h4 className="font-medium">Requisitos:</h4>
                <p className="text-sm text-gray-600">{job.requirements}</p>
              </div>
            )}
            {job.employer_name && (
              <p className="text-sm text-gray-500">Publicada por: {job.employer_name} (proprietário verificado)</p>
            )}

            {/* Contato — gated por cadastro + referência */}
            <div className="border-t pt-4" data-testid="public-job-contact-section">
              {contact ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="public-job-contact-revealed">
                  {contact.kind === "externa" ? (
                    <>
                      <p className="font-semibold text-green-900">📱 Contato: {contact.contact || "não informado"}</p>
                      {contact.submitter_name && (
                        <p className="text-xs text-green-700 mt-1">Indicada por {contact.submitter_name}</p>
                      )}
                      {contact.source_url && (
                        <a href={contact.source_url} target="_blank" rel="noreferrer" className="text-sm text-green-700 underline">
                          Ver anúncio original →
                        </a>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-green-900">💬 {contact.employer_name}</p>
                      <p className="text-sm text-green-700 mt-1">{contact.message}</p>
                      <Link to="/">
                        <Button size="sm" className="mt-2 bg-green-700 hover:bg-green-800" data-testid="public-job-open-app">
                          Abrir o app →
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              ) : needsReference ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="public-job-reference-form">
                  <p className="font-semibold text-amber-900">🤝 Falta só um passo: informe uma referência</p>
                  <p className="text-sm text-amber-700 mt-1 mb-3">
                    Para proteger quem contrata, o contato é liberado após você informar um ex-empregador (ou alguém para quem já trabalhou) com telefone.
                  </p>
                  <form onSubmit={submitReference} className="space-y-2">
                    <Input
                      placeholder="Nome do ex-empregador *"
                      value={refForm.employer_name}
                      onChange={(e) => setRefForm({ ...refForm, employer_name: e.target.value })}
                      required
                      data-testid="reference-employer-name"
                    />
                    <Input
                      placeholder="Telefone com DDD * (ex: 17 99999-0000)"
                      value={refForm.employer_phone}
                      onChange={(e) => setRefForm({ ...refForm, employer_phone: e.target.value })}
                      required
                      data-testid="reference-employer-phone"
                    />
                    <Input
                      placeholder="Fazenda/propriedade (opcional)"
                      value={refForm.property_name}
                      onChange={(e) => setRefForm({ ...refForm, property_name: e.target.value })}
                      data-testid="reference-property-name"
                    />
                    {refError && <p className="text-sm text-red-600" data-testid="reference-error">{refError}</p>}
                    <Button type="submit" disabled={saving} className="w-full bg-green-700 hover:bg-green-800" data-testid="reference-submit-btn">
                      {saving ? "Salvando..." : "Salvar referência e ver o contato →"}
                    </Button>
                  </form>
                  <p className="text-xs text-amber-700 mt-3">
                    💡 Dentro do app você também pode pedir uma <strong>recomendação verificada</strong> ao seu ex-empregador — ela vale como referência e fortalece seu perfil.
                  </p>
                </div>
              ) : token ? (
                <Button onClick={revealContact} className="w-full bg-green-700 hover:bg-green-800" data-testid="public-job-reveal-contact">
                  🔓 Ver contato da vaga
                </Button>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid="public-job-register-cta">
                  <Lock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                  <p className="font-semibold text-amber-900">O contato desta vaga é exclusivo para cadastrados</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Cadastre-se grátis, informe suas referências de trabalho e acesse esta e outras oportunidades de emprego e ofertas de trabalho.
                  </p>
                  <Link
                    to="/?cadastro=vaga"
                    onClick={() => localStorage.setItem("pending_vaga", JSON.stringify({ id: jobId, ts: Date.now() }))}
                  >
                    <Button className="mt-4 w-full sm:w-auto bg-green-700 hover:bg-green-800" data-testid="public-job-register-btn">
                      Cadastrar grátis e ver o contato →
                    </Button>
                  </Link>
                  <p className="text-xs text-amber-600 mt-2">Depois do cadastro você volta direto para esta vaga. 😉</p>
                </div>
              )}
            </div>

            <div className="border-t pt-3">
              <p className="text-xs text-gray-500">Conseguiu esse trabalho por aqui? Conta pra gente:</p>
              <HiredSignalBox jobId={jobId} />
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-500 pb-6">
          HeveaTalent — o canal de comunicação da heveicultura · <Link to="/balcao" className="underline">Divulgue sua vaga grátis no Balcão</Link>
        </p>
      </main>
    </div>
  );
}
